// src/utils/generateTube.js
import * as THREE from "three";

function lerp(a, b, t) {
  return a * (1 - t) + b * t;
}

// simple 5-point spline (bottom, midDown, mid, midUp, top)
function spline5([b, md, m, mu, t], x) {
  if (x < 0.25) return lerp(b, md, x / 0.25);
  if (x < 0.5) return lerp(md, m, (x - 0.25) / 0.25);
  if (x < 0.75) return lerp(m, mu, (x - 0.5) / 0.25);
  return lerp(mu, t, (x - 0.75) / 0.25);
}

function ngonPoint(i, n) {
  const a = (i / n) * Math.PI * 2;
  return new THREE.Vector2(Math.cos(a), Math.sin(a));
}

function morphToCircle(v, morph) {
  const len = v.length();
  if (len === 0) return v;
  return v.clone().normalize().multiplyScalar(lerp(len, 1, morph));
}

/**
 * generateTube - Creates a watertight parametric vessel
 * Returns { positions, indices, metrics }
 */
export function generateTube({
  height = 180,
  sections = [50, 50, 50, 50, 50],
  wall = 4,
  base = 6,
  sides = 6,
  morph = 1,
  twistDeg = 0,
  verticalSeg = 80,
  radialSeg = 96,
  graph = null,
}) {
  const ringsOuter = [];
  const ringsInner = [];
  const twistRad = THREE.MathUtils.degToRad(twistDeg);
  const graphSample = (t) =>
    graph
      ? graph[Math.min(graph.length - 1, Math.round(t * (graph.length - 1)))]
      : 1;

  // Determine where the inner wall starts (at baseY, inner wall begins)
  const baseY = Math.min(height - 1, Math.max(1, base));

  // Build outer and inner rings from bottom to top
  for (let j = 0; j <= verticalSeg; j++) {
    const t = j / verticalSeg;
    const y = t * height;
    const r = spline5(sections, t) * graphSample(t);
    const rot = twistRad * t;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);

    // Outer ring
    const ringO = [];
    for (let i = 0; i < radialSeg; i++) {
      const basePt = morphToCircle(ngonPoint(i, sides), morph);
      const vx = basePt.x * r;
      const vz = basePt.y * r;
      ringO.push(
        new THREE.Vector3(
          vx * cos - vz * sin,
          y,
          vx * sin + vz * cos
        )
      );
    }
    ringsOuter.push(ringO);

    // Inner ring - only exists above baseY
    if (y >= baseY) {
      const innerR = Math.max(1, r - wall);
      const ringI = [];
      for (let i = 0; i < radialSeg; i++) {
        const basePt = morphToCircle(ngonPoint(i, sides), morph);
        const ix = basePt.x * innerR;
        const iz = basePt.y * innerR;
        ringI.push(
          new THREE.Vector3(
            ix * cos - iz * sin,
            y,
            ix * sin + iz * cos
          )
        );
      }
      ringsInner.push(ringI);
    }
  }

  const pos = [];
  const idx = [];

  // Helper: add a vertex and return its index
  function addVertex(v) {
    const i = pos.length / 3;
    pos.push(v.x, v.y, v.z);
    return i;
  }

  // Helper: add a ring of vertices and return start index
  function addRing(ring) {
    const start = pos.length / 3;
    ring.forEach((v) => pos.push(v.x, v.y, v.z));
    return start;
  }

  // 1) BUILD OUTER SURFACE (front-facing)
  const outerStart = pos.length / 3;
  ringsOuter.forEach((r) => r.forEach((v) => pos.push(v.x, v.y, v.z)));
  
  for (let j = 0; j < ringsOuter.length - 1; j++) {
    const a = outerStart + j * radialSeg;
    const b = a + radialSeg;
    for (let k = 0; k < radialSeg; k++) {
      const k2 = (k + 1) % radialSeg;
      // CCW winding for outward normals
      idx.push(a + k, b + k, b + k2);
      idx.push(a + k, b + k2, a + k2);
    }
  }

  // 2) BUILD INNER SURFACE (reversed winding for inward normals)
  const innerStart = pos.length / 3;
  ringsInner.forEach((r) => r.forEach((v) => pos.push(v.x, v.y, v.z)));
  
  for (let j = 0; j < ringsInner.length - 1; j++) {
    const a = innerStart + j * radialSeg;
    const b = a + radialSeg;
    for (let k = 0; k < radialSeg; k++) {
      const k2 = (k + 1) % radialSeg;
      // CW winding for inward normals
      idx.push(a + k, b + k2, b + k);
      idx.push(a + k, a + k2, b + k2);
    }
  }

  // 3) BUILD RIM (bridge top outer to top inner)
  const topOuterIdx = outerStart + (ringsOuter.length - 1) * radialSeg;
  const topInnerIdx = innerStart + (ringsInner.length - 1) * radialSeg;
  
  for (let k = 0; k < radialSeg; k++) {
    const k2 = (k + 1) % radialSeg;
    // Rim strip - connects outer top to inner top
    idx.push(topOuterIdx + k, topInnerIdx + k, topInnerIdx + k2);
    idx.push(topOuterIdx + k, topInnerIdx + k2, topOuterIdx + k2);
  }

  // 4) BUILD BASE
  // Find the outer ring at baseY (first ring above or at baseY)
  let baseRingIdx = 0;
  for (let j = 0; j < ringsOuter.length; j++) {
    if (ringsOuter[j][0].y >= baseY) {
      baseRingIdx = j;
      break;
    }
  }

  const outerBottomRing = ringsOuter[0]; // y = 0
  const outerBaseRing = ringsOuter[baseRingIdx]; // y = baseY
  const innerBaseRing = ringsInner[0]; // first inner ring (at baseY)

  // Add base rings as new vertices for proper normals
  const outerBottomStart = addRing(outerBottomRing);
  const outerBaseStart = addRing(outerBaseRing);
  const innerBaseStart = addRing(innerBaseRing);

  // 4a) Outer side wall from y=0 to y=baseY
  for (let k = 0; k < radialSeg; k++) {
    const k2 = (k + 1) % radialSeg;
    idx.push(outerBottomStart + k, outerBaseStart + k, outerBaseStart + k2);
    idx.push(outerBottomStart + k, outerBaseStart + k2, outerBottomStart + k2);
  }

  // 4b) Base plate annulus (between outer and inner at baseY)
  for (let k = 0; k < radialSeg; k++) {
    const k2 = (k + 1) % radialSeg;
    // Top face of base plate
    idx.push(outerBaseStart + k, innerBaseStart + k, innerBaseStart + k2);
    idx.push(outerBaseStart + k, innerBaseStart + k2, outerBaseStart + k2);
  }

  // 4c) Bottom disc at y=0 (close the bottom)
  const centerBottom = addVertex(new THREE.Vector3(0, 0, 0));
  for (let k = 0; k < radialSeg; k++) {
    const k2 = (k + 1) % radialSeg;
    // Bottom face triangles (CW from bottom view)
    idx.push(centerBottom, outerBottomStart + k2, outerBottomStart + k);
  }

  // Calculate bounding box
  let minx = 1e9, miny = 1e9, minz = 1e9;
  let maxx = -1e9, maxy = -1e9, maxz = -1e9;
  
  for (let i = 0; i < pos.length; i += 3) {
    const x = pos[i], y = pos[i + 1], z = pos[i + 2];
    if (x < minx) minx = x;
    if (y < miny) miny = y;
    if (z < minz) minz = z;
    if (x > maxx) maxx = x;
    if (y > maxy) maxy = y;
    if (z > maxz) maxz = z;
  }

  // Calculate metrics using proper volume/surface formulas
  let shellVolume = 0;
  let outerSurfaceArea = 0;
  let innerSurfaceArea = 0;
  
  for (let j = 0; j < verticalSeg; j++) {
    const t = j / verticalSeg;
    const t2 = (j + 1) / verticalSeg;
    const r1 = spline5(sections, t) * graphSample(t);
    const r2 = spline5(sections, t2) * graphSample(t2);
    const h = height / verticalSeg;
    
    const innerR1 = Math.max(0, r1 - wall);
    const innerR2 = Math.max(0, r2 - wall);
    
    // Volume
    const outerVol = (Math.PI * h / 3) * (r1 * r1 + r1 * r2 + r2 * r2);
    const innerVol = (Math.PI * h / 3) * (innerR1 * innerR1 + innerR1 * innerR2 + innerR2 * innerR2);
    shellVolume += outerVol - innerVol;
    
    // Surface area
    const slantHeight = Math.sqrt(h * h + (r2 - r1) * (r2 - r1));
    outerSurfaceArea += Math.PI * (r1 + r2) * slantHeight;
    
    if (t >= baseY / height && (innerR1 > 0 || innerR2 > 0)) {
      const innerSlantHeight = Math.sqrt(h * h + (innerR2 - innerR1) * (innerR2 - innerR1));
      innerSurfaceArea += Math.PI * (innerR1 + innerR2) * innerSlantHeight;
    }
  }
  
  // Add base volume
  const baseR = spline5(sections, 0) * graphSample(0);
  const baseInnerR = Math.max(0, baseR - wall);
  shellVolume += Math.PI * baseY * (baseR * baseR - baseInnerR * baseInnerR);
  
  // Add inner base surface
  if (baseInnerR > 0) {
    innerSurfaceArea += Math.PI * baseInnerR * baseInnerR;
  }

  return {
    positions: new Float32Array(pos),
    indices: new Uint32Array(idx),
    metrics: {
      bbox_mm: [maxx - minx, maxy - miny, maxz - minz],
      volume_mm3: shellVolume,
      volume_cm3: shellVolume / 1000,
      surfaceArea_mm2: outerSurfaceArea + innerSurfaceArea,
      surfaceArea_cm2: (outerSurfaceArea + innerSurfaceArea) / 100,
      outerSurfaceArea_mm2: outerSurfaceArea,
      outerSurfaceArea_cm2: outerSurfaceArea / 100,
      innerSurfaceArea_mm2: innerSurfaceArea,
      innerSurfaceArea_cm2: innerSurfaceArea / 100,
    },
  };
}
